/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 18/02/22
 */

import React from 'react';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { Tile, TileHeading } from '@/components/Tile';
import { Flex, GridItem } from '@chakra-ui/react';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import WorkloadChart, { WorkloadChartProps } from '@/components/Charts/WorkloadChart';
import { mapWorkloadStaffTypeToString, WorkloadStaffType } from '@/wpt-lib/interfaces/Workplan';
import FocusHeader from '@/components/FocusHeader/FocusHeader';

const AllocationsTile = ({
    action,
    workplanVersion,
    loading,
    error,
}: Partial<WorkloadChartProps> & {
    action?: React.ReactElement;
    loading: boolean;
    error: boolean;
}) => {
    if (workplanVersion?.fte === 0) {
        return (
            <GridItem bg="offWhite" p={6} rounded="lg" colSpan={{ base: 2, lg: 1 }} role="landmark">
                <TileHeading heading={'NO WORKLOAD BREAKDOWN'} />
                <Flex h="100%" justifyContent="center" alignItems="center">
                    <SimpleAlert variant="left-accent" status="warning">
                        Assignment does not have workplan.
                    </SimpleAlert>
                </Flex>
            </GridItem>
        );
    }

    return (
        <Tile
            heading={
                <FocusHeader as="h3" id="Allocation-Heading" size={'small'} textColor="blue.500">
                    Summary of Workload Allocation
                </FocusHeader>
            }
            colspan={{ base: 2, lg: 1 }}
            action={action}
            role="landmark"
        >
            <Flex alignItems="center" gap={5} my={3}>
                <p>
                    Staff Type: {mapWorkloadStaffTypeToString[workplanVersion?.staffType ?? WorkloadStaffType.ACADEMIC]}
                </p>
                <p>Current FTE: {workplanVersion?.fte}</p>
            </Flex>

            <StateDisplay
                loading={loading}
                error={error}
                data={workplanVersion}
                errorRender={() => <SimpleAlert variant="subtle">Could not load Workplan summary</SimpleAlert>}
                render={(workplanVersion) => <WorkloadChart workplanVersion={workplanVersion} />}
            />
        </Tile>
    );
};

export default AllocationsTile;
